import { nanoid } from "nanoid";
import icons from "../assets/icons";
import { Property } from "../types";

/**
 * Creates a jQuery-like function for selecting elements in the DOM.
 *
 * @param {string} selector - The CSS selector to match elements against.
 * @return {HTMLElement | NodeList | null} The selected element, a list of elements, or null if no elements matched.
 */
const $ = (selector: string) => {
  const elems = document.querySelectorAll(selector);

  if (elems.length === 1) return elems[0];
  if (elems.length > 1) return elems;
  return null;
};

/**
 * Function to create an HTML element with optional children, attributes, and events.
 *
 * @param {keyof HTMLElementTagNameMap} type - the type of HTML element to create
 * @param {string | HTMLElement[]} children - optional children to append to the element
 * @param {Record<string, string>} attributes - optional attributes to set on the element
 * @param {Record<string, EventListener>} events - optional event listeners to add to the element
 * @return {HTMLElement} the created HTML element
 */
const $createEl = (
  type: keyof HTMLElementTagNameMap,
  children?: string | HTMLElement[],
  attributes?: Record<string, string>,
  events?: Record<string, EventListener>
) => {
  const elem = document.createElement(type);

  if (children) {
    if (typeof children === "string") {
      elem.innerHTML = children;
    } else {
      elem.append(...children);
    }
  }

  if (attributes) {
    for (const key in attributes) {
      elem.setAttribute(key, attributes[key]);
    }
  }

  if (events) {
    for (const key in events) {
      elem.addEventListener(key, events[key]);
    }
  }

  return elem;
};

/**
 * Create a block element with the specified id and component type.
 *
 * @param {string} id - The unique identifier for the block element.
 * @param {string} compType - The type of the component.
 * @return {Element} The newly created block element.
 */
const creatBlock = (id: string, compType: string) => {
  return $createEl(
    "div",
    "",
    {
      draggable: "true",
      id: id,
      class: "block",
    },
    {
      dragstart: (e: Event) => {
        e.stopPropagation();
        if (e.target instanceof HTMLElement) {
          e.target.style.opacity = "0.5";
        }

        (e as DragEvent).dataTransfer?.setData("text", compType);
      },
      dragend: (e: Event) => {
        if (e.target instanceof HTMLElement) {
          e.target.style.opacity = "1";
        }
      },
    }
  );
};

/**
 * Creates a label element with the specified text content and optional for attribute.
 *
 * @param {string} label - The text content of the label.
 * @param {string} [htmlFor] - The value of the for attribute.
 * @return {Element} The created label element.
 */
const createLabel = (label: string, htmlFor?: string) => {
  return $createEl("label", label, htmlFor ? { for: htmlFor } : {});
};

/**
 * Creates an input element with the specified name, type, id, and additional attributes.
 *
 * @param {string} name - the name attribute of the input element
 * @param {string} [type="text"] - the type attribute of the input element
 * @param {string} [id] - the id attribute of the input element
 * @param {Record<string, string>} [attr={}] - additional attributes of the input element
 * @return {HTMLElement} the created input element
 */
const createInput = (
  name: string,
  type: string = "text",
  id?: string,
  attr: Record<string, string> = {}
) => {
  let placeholder = name;

  if (name.includes("-")) {
    placeholder = name.split("-")[1];
  }

  return $createEl("input", "", {
    type,
    name,
    id: id || "",
    autocomplete: "off",
    placeholder,
    ...attr,
  });
};

/**
 * Creates a wrapper element with the given child elements and attributes.
 *
 * @param {HTMLElement[]} childs - an array of child elements
 * @param {Record<string, string>} [attributes={}] - an object representing attributes for the wrapper element
 * @return {HTMLElement} the newly created wrapper element
 */
const wrapper = (
  childs: HTMLElement[],
  attributes: Record<string, string> = {}
) => {
  return $createEl("div", [...childs], attributes);
};

/**
 * Creates a input (default) property element with a label and input field.
 *
 * @param {string} label - The label text for the property.
 * @param {string} propertyName - The name of the property.
 * @param {string} defaultValue - The default value of the property.
 * @return {HTMLElement} The created property element.
 */
const createDftProperty = (
  label: string,
  propertyName: string,
  defaultValue: string = ""
) => {
  let id = "p" + nanoid(10);
  return $createEl(
    "div",
    [
      createLabel(label, id),
      createInput(propertyName, "text", id, {
        defaultValue,
      }),
    ],
    {
      class: "property",
    }
  );
};

/**
 * Generate a multi-input property with label and options.
 *
 * @param {string} label - The label for the input property
 * @param {string} propertyName - The name of the property
 * @param {string[]} options - An array of options for the multi-input
 */
const createMultiInputProperty = (
  label: string,
  propertyName: string,
  options: string[]
) => {
  let id = "p" + nanoid(10);
  const inputs = options.map((option) => {
    let id = "p" + "-mi-" + nanoid(10);
    return createInput(propertyName + "-" + option, "text", id, {
      defaultValue: "",
    });
  });

  const linkIcon = $createEl("div", icons.link);

  const linkButton = $createEl("button", [linkIcon], {
    class: "link-button",
  });

  inputs.push(linkButton);

  return $createEl(
    "div",
    [createLabel(label, id), wrapper(inputs, { class: "multi-input" })],
    {
      class: "property multi-input-property",
    }
  );
};

/**
 * Creates a Color property element with color input based on provided label and property name.
 *
 * @param {string} label - the label for the property
 * @param {string} propertyName - the name of the property
 * @return {HTMLElement} the CLR property element
 */
const createClrProperty = (label: string, propertyName: string) => {
  let id = "p" + nanoid(10);
  return $createEl(
    "div",
    [
      createLabel(label, id),
      createInput(propertyName, "text", id, {
        "data-coloris": "",
        defaultValue: "",
      }),
    ],
    {
      class: "property",
    }
  );
};

/**
 * Creates a property element with radio input options based on the provided label, propertyName, and options.
 *
 * @param {string} label - The label for the property.
 * @param {string} propertyName - The name of the property.
 * @param {string[]} options - An array of options for the radio inputs.
 * @param {string} defaultValue - the default value
 * @return {HTMLElement} The created property element.
 */
const createRdoPorerty = (
  label: string,
  propertyName: string,
  options: string[],
  defaultValue?: string
): HTMLElement => {
  const optionsElems = [];

  for (const option of options) {
    let id = "p" + nanoid(10);
    optionsElems.push(
      createInput(propertyName, "radio", id),
      createLabel(option, id)
    );
  }

  const childsWithWrapper = wrapper([...optionsElems]);

  return $createEl("div", [createLabel(label), childsWithWrapper], {
    class: "property radio",
  });
};

/**
 * Creates a select element with options based on the provided property name and options array.
 *
 * @param {string} propertyName - the name of the property associated with the select element
 * @param {string[]} options - an array of strings representing the options for the select element
 * @return {HTMLElement} a select element populated with the given options
 */
const createSelect = (
  propertyName: string,
  options: string[],
  defaultValue: string = ""
) => {
  const optionsElems = [];

  for (const option of options) {
    optionsElems.push(
      $createEl("option", option, {
        value: option,
      })
    );
  }

  return $createEl("select", optionsElems, {
    name: propertyName,
    id: "p" + nanoid(10),
    defaultValue: defaultValue || options[0],
  });
};

/**
 * Creates an Select property with a label, property name, and options.
 *
 * @param {string} label - the label for the property
 * @param {string} propertyName - the name of the property
 * @param {string[]} options - the options for the select element
 * @param {string} defaultValue - the default value for the select element
 * @return {HTMLElement} the created div element with the property select class
 */
const createSltProperty = (
  label: string,
  propertyName: string,
  options: string[],
  defaultValue?: string
) => {
  return $createEl(
    "div",
    [createLabel(label), createSelect(propertyName, options, defaultValue)],
    {
      class: "property select",
    }
  );
};

/**
 * Creates a style sector based on the given group name and properties.
 *
 * @param {string} groupName - The name of the group.
 * @param {Property[]} properties - An array of properties.
 * @return {HTMLElement} The created details element with title and properties.
 */
const createStyleSector = (groupName: string, properties: Property[]) => {
  const titleEl = $createEl("summary", groupName);
  const propertiesElems = [];
  for (const property of properties) {
    if (property.type === "radio") {
      propertiesElems.push(
        createRdoPorerty(
          property.name,
          property.property,
          property.options,
          property?.defaultValue
        )
      );
    } else if (property.type === "select") {
      propertiesElems.push(
        createSltProperty(
          property.name,
          property.property,
          property.options,
          property?.defaultValue
        )
      );
    } else if (property.type === "color") {
      propertiesElems.push(createClrProperty(property.name, property.property));
    } else if (property.type === "multi-input") {
      propertiesElems.push(
        createMultiInputProperty(
          property.name,
          property.property,
          property.options
        )
      );
    } else {
      propertiesElems.push(
        createDftProperty(
          property.name,
          property.property,
          property?.defaultValue
        )
      );
    }
  }

  const propertiesWithWrapper = wrapper([...propertiesElems]);

  return $createEl("details", [titleEl, propertiesWithWrapper], {
    class: "details",
  });
};

export {
  $,
  $createEl,
  creatBlock,
  createLabel,
  createInput,
  wrapper,
  createDftProperty,
  createMultiInputProperty,
  createClrProperty,
  createRdoPorerty,
  createSltProperty,
  createStyleSector,
};
